import type { AuthTokens, LoginRequest, User } from '@greensm/api-client';

export type AuthStatus = 'booting' | 'anonymous' | 'authenticated' | 'expired';
export interface AuthState {
  status: AuthStatus;
  user: User | null;
}

/** Cổng gọi API định danh. Tách riêng để không phụ thuộc vòng với http client. */
export interface AuthGateway {
  login(body: LoginRequest): Promise<{ tokens?: AuthTokens; user: User }>;
  refresh(refreshToken: string): Promise<AuthTokens | undefined>;
  me(): Promise<User>;
  logout(refreshToken: string): Promise<void>;
}

export interface RefreshTokenStorage {
  get(): string | null;
  set(token: string): void;
  clear(): void;
}

export interface AuthStoreOptions {
  gateway: AuthGateway;
  storage?: RefreshTokenStorage;
  cookieSession?: boolean;
  /**
   * Gọi khi người dùng đổi (đăng nhập tài khoản khác / đăng xuất / hết phiên).
   * Nơi dọn cache để không rò thông tin tài khoản cũ.
   */
  onUserChange?: (prev: User | null, next: User | null) => void;
}

export interface AuthStore {
  getState(): AuthState;
  subscribe(listener: () => void): () => void;
  getAccessToken(): string | null;
  /** Khôi phục phiên khi mở app (dùng refresh token nếu còn). */
  restore(): Promise<void>;
  login(body: LoginRequest): Promise<User>;
  logout(): Promise<void>;
  /** Dành cho http client: làm mới access token. */
  refresh(): Promise<boolean>;
  /** Dành cho http client: phiên không cứu được nữa. */
  markExpired(): void;
}

export const sessionRefreshStorage = (key = 'gsm.rt'): RefreshTokenStorage => ({
  get: () => { try { return sessionStorage.getItem(key); } catch { return null; } },
  set: (t) => { try { sessionStorage.setItem(key, t); } catch { /* noop */ } },
  clear: () => { try { sessionStorage.removeItem(key); } catch { /* noop */ } },
});

export function createAuthStore(options: AuthStoreOptions): AuthStore {
  const { gateway, storage = sessionRefreshStorage(), onUserChange } = options;
  let accessToken: string | null = null; // chỉ trong bộ nhớ
  let state: AuthState = { status: 'booting', user: null };
  const listeners = new Set<() => void>();

  const set = (next: AuthState) => {
    const prevUser = state.user;
    state = next;
    if ((prevUser?.id ?? null) !== (next.user?.id ?? null)) onUserChange?.(prevUser, next.user);
    listeners.forEach((l) => l());
  };

  const applyTokens = (t: AuthTokens | undefined) => {
    if (!t || options.cookieSession) { accessToken = null; storage.clear(); return; }
    accessToken = t.accessToken;
    storage.set(t.refreshToken);
  };

  return {
    getState: () => state,
    subscribe(l) { listeners.add(l); return () => listeners.delete(l); },
    getAccessToken: () => accessToken,

    async restore() {
      const rt = storage.get();
      if (!rt && !options.cookieSession) return set({ status: 'anonymous', user: null });
      try {
        if (!options.cookieSession) applyTokens(await gateway.refresh(rt!));
        set({ status: 'authenticated', user: await gateway.me() });
      } catch {
        accessToken = null;
        storage.clear();
        set({ status: 'anonymous', user: null });
      }
    },

    async login(body) {
      const { tokens, user } = await gateway.login(body); // lỗi ⇒ ném cho form hiển thị
      applyTokens(tokens);
      set({ status: 'authenticated', user });
      return user;
    },

    async logout() {
      const rt = storage.get();
      accessToken = null;
      storage.clear();
      set({ status: 'anonymous', user: null }); // UI về đăng nhập ngay, không chờ mạng
      if (rt || options.cookieSession) await gateway.logout(rt ?? '').catch(() => undefined);
    },

    async refresh() {
      const rt = storage.get();
      if (!rt && !options.cookieSession) return false;
      try {
        applyTokens(await gateway.refresh(rt ?? ''));
        return true;
      } catch {
        return false;
      }
    },

    markExpired() {
      accessToken = null;
      storage.clear();
      // giữ user=null để mọi dữ liệu cũ bị dọn qua onUserChange
      set({ status: 'expired', user: null });
    },
  };
}
